import { FONT_REGISTRY } from './fontList';
import { useEditor } from '../state/editorStore';

const ANCHORS = ['top', 'upper', 'center', 'lower', 'bottom'] as const;

/** Curated customization controls - a deliberately small set, not 300 knobs. */
export function StyleControls() {
  const style = useEditor((s) => s.resolvedStyle());
  const placement = useEditor((s) => s.resolvedPlacement());
  const updateStyle = useEditor((s) => s.updateStyle);
  const setPlacement = useEditor((s) => s.setPlacement);
  const reset = useEditor((s) => s.resetStyle);

  return (
    <div className="style-controls">
      <div className="ctrl">
        <label>Font</label>
        <select
          value={style.fontFamily}
          onChange={(e) => updateStyle({ fontFamily: e.target.value })}
        >
          {FONT_REGISTRY.map((f) => (
            <option key={f.family} value={f.family}>
              {f.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="ctrl">
        <label>Size {Math.round(style.fontSizePx)}</label>
        <input
          type="range"
          min={30}
          max={140}
          value={style.fontSizePx}
          onChange={(e) => updateStyle({ fontSizePx: Number(e.target.value) })}
        />
      </div>

      <div className="ctrl">
        <label>Weight {style.fontWeight}</label>
        <input
          type="range"
          min={400}
          max={900}
          step={100}
          value={style.fontWeight}
          onChange={(e) => updateStyle({ fontWeight: Number(e.target.value) })}
        />
      </div>

      <div className="ctrl inline">
        <label>Text</label>
        <input
          type="color"
          value={toHex(style.color)}
          onChange={(e) => updateStyle({ color: e.target.value })}
        />
        <label>Active</label>
        <input
          type="color"
          value={toHex(style.activeWord.color)}
          onChange={(e) => updateStyle({ activeWord: { color: e.target.value } })}
        />
      </div>

      <div className="ctrl">
        <label>Line spacing {style.lineHeight.toFixed(2)}</label>
        <input
          type="range"
          min={0.9}
          max={1.8}
          step={0.05}
          value={style.lineHeight}
          onChange={(e) => updateStyle({ lineHeight: Number(e.target.value) })}
        />
      </div>

      <div className="ctrl">
        <label>Caption width {Math.round(placement.maxWidthNorm * 100)}%</label>
        <input
          type="range"
          min={0.4}
          max={1}
          step={0.02}
          value={placement.maxWidthNorm}
          onChange={(e) => setPlacement({ maxWidthNorm: Number(e.target.value) })}
        />
      </div>

      <div className="ctrl">
        <label>Position</label>
        <div className="anchor-row">
          {ANCHORS.map((a) => (
            <button
              key={a}
              className={placement.anchor === a && placement.yNorm === 0 ? 'chip on' : 'chip'}
              onClick={() => setPlacement({ anchor: a, yNorm: 0 })}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      <div className="ctrl">
        <label>Animation intensity {style.entrance.intensity.toFixed(1)}</label>
        <input
          type="range"
          min={0}
          max={2}
          step={0.1}
          value={style.entrance.intensity}
          onChange={(e) =>
            updateStyle({
              entrance: { intensity: Number(e.target.value) },
              activeWord: { animation: { intensity: Number(e.target.value) } },
            })
          }
        />
      </div>

      <button className="reset" onClick={reset}>
        Reset to template defaults
      </button>
    </div>
  );
}

function toHex(c: string): string {
  return c.startsWith('#') ? c.slice(0, 7) : '#ffffff';
}
